/**
 * Native (Capacitor) bootstrap.
 *
 * All of this is a no-op on the web build: `isNativeApp()` is false, so nothing
 * runs and the Capacitor plugins are never invoked. On iOS/Android it:
 *   1. Wires secure token storage (Keychain/Keystore via @capacitor/preferences)
 *      into services/authToken, and hydrates the in-memory token at startup so
 *      the axios interceptor can attach `Authorization: Bearer` synchronously.
 *   2. Registers for native push and reports the device token to the backend.
 *   3. Handles magic-link deep links (Universal Links / App Links) by extracting
 *      the token and running the verify flow.
 *
 * Capacitor is imported dynamically so the web bundle doesn't hard-require the
 * native runtime.
 */
import { hydrateToken, setTokenPersistor } from '../services/authToken'

const TOKEN_KEY = 'vc_attendee_token'

/** True when running inside the Capacitor native shell (iOS/Android). */
export function isNativeApp(): boolean {
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor
  return Boolean(cap?.isNativePlatform?.())
}

/** Initialize native integrations. Safe to call unconditionally (no-op on web). */
export async function initNative(): Promise<void> {
  if (!isNativeApp()) return
  await initSecureTokenStore()
  await initDeepLinks()
  // Not awaited: the permission request waits on the user answering the system
  // alert, and first render must not block on it (blank screen behind the alert).
  initPushNotifications().catch((err) => {
    console.warn('Push init failed', err)
  })
}

async function initSecureTokenStore(): Promise<void> {
  const { Preferences } = await import('@capacitor/preferences')
  // Persist future tokens to secure storage.
  setTokenPersistor({
    save: async (token: string) => {
      await Preferences.set({ key: TOKEN_KEY, value: token })
    },
    clear: async () => {
      await Preferences.remove({ key: TOKEN_KEY })
    },
  })
  // Hydrate the token saved from a previous session so the interceptor has it.
  const { value } = await Preferences.get({ key: TOKEN_KEY })
  hydrateToken(value ?? null)
}

async function initPushNotifications(): Promise<void> {
  const { PushNotifications } = await import('@capacitor/push-notifications')
  const { Capacitor } = await import('@capacitor/core')

  const perm = await PushNotifications.requestPermissions()
  if (perm.receive !== 'granted') return

  PushNotifications.addListener('registration', (token) => {
    void registerDeviceToken(Capacitor.getPlatform(), token.value)
  })
  PushNotifications.addListener('registrationError', (err) => {
    // Non-fatal: the app still works without push.
    console.warn('Push registration error', err)
  })
  await PushNotifications.register()
}

async function registerDeviceToken(platform: string, deviceToken: string): Promise<void> {
  // Lazy imports to avoid a cycle and to keep this native-only path isolated.
  const { apiClient } = await import('../services/api')
  const { resolveEventId } = await import('../services/eventService')
  try {
    const eventId = await resolveEventId()
    await apiClient.post(`/events/${eventId}/push/devices`, {
      platform: platform === 'ios' ? 'ios' : 'android',
      deviceToken,
    })
  } catch {
    // Best-effort; a failed registration must not break app startup.
  }
}

async function initDeepLinks(): Promise<void> {
  const { App } = await import('@capacitor/app')
  App.addListener('appUrlOpen', (event) => {
    void handleDeepLink(event.url)
  })
}

/**
 * Parse a magic-link deep link (`.../e/:slug/verify?token=...`) and run verify.
 * Exported for testing.
 */
export async function handleDeepLink(url: string): Promise<boolean> {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return false
  }
  const match = parsed.pathname.match(/\/e\/([^/]+)\/verify$/)
  if (!match) return false
  const token = parsed.searchParams.get('token')
  if (!token) return false

  const { attendeeAuth } = await import('../services/attendeeAuth')
  const { setActiveEventSlug } = await import('../services/eventService')
  setActiveEventSlug(decodeURIComponent(match[1]))
  try {
    await attendeeAuth.verify(token)
    // Navigate into the event; the router picks up the session on next render.
    window.location.hash = ''
    window.location.pathname = `/e/${match[1]}`
    return true
  } catch {
    return false
  }
}
